import { BreakingTier, isDiggableForTier } from "./ai/breakingTier";
import type { Board } from "./board";
import { Chasm } from "./things/chasm";
import { LavaPool } from "./things/lavaPool";
import { WoodenDoor } from "./things/woodenDoor";
import type { XY } from "./xy";

/**
 * Decides which cells an actor may move through, for distance map building.
 * `key` must uniquely identify the rule — it is used in per-turn cache keys.
 */
export interface PassabilityRule {
	readonly key: string;
	isPassable(board: Board, xy: XY): boolean;
}

const WALKING: PassabilityRule = {
	key: "walking",
	isPassable: (board, xy) => board.isPassable(xy),
};

/**
 * A door cell is traversable only when the door is the *only* thing blocking it —
 * anything else that landed there (a corpse, a frozen mob, a pushed obstacle) still
 * blocks, since bumping the door open wouldn't clear it.
 */
export function isPassableThroughDoors(board: Board, xy: XY): boolean {
	if (board.isPassable(xy)) return true;
	if (!board.isInBounds(xy)) return false;
	return board.getThingsAt(xy).every((t) => !t.blocksMovement || t instanceof WoodenDoor);
}

const THROUGH_DOORS: PassabilityRule = {
	key: "doors",
	isPassable: isPassableThroughDoors,
};

const FLYING: PassabilityRule = {
	key: "flying",
	isPassable: (board, xy) => {
		if (board.isPassable(xy)) return true;
		if (!board.isInBounds(xy)) return false;
		return board.getThingsAt(xy).some((t) => t instanceof Chasm || t instanceof LavaPool);
	},
};

function breakingTier(tier: BreakingTier): PassabilityRule {
	if (tier === BreakingTier.NONE) return THROUGH_DOORS;
	return {
		key: `breaking_${tier}`,
		isPassable: (board, xy) => {
			if (board.isPassable(xy)) return true;
			if (!board.isInBounds(xy)) return false;
			const blockers = board.getThingsAt(xy).filter((t) => t.blocksMovement);
			if (blockers.length === 0) return false;
			return blockers.every((t) => t instanceof WoodenDoor || isDiggableForTier(t, tier));
		},
	};
}

export const Passability = {
	WALKING,
	THROUGH_DOORS,
	FLYING,
	breakingTier,
};
